import { ThemeComponent } from '@/theme/ThemeComponent';
import { BaseComponent, Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ManualOptionSelector, OptionSelector } from '@/utils/OptionSelectors';
import { ProductButtonOption } from './ProductButtonOption';
import { ISimpleEvent } from 'strongly-typed-events';

export interface ProductOption extends BaseComponent {
    selected: string|null;
    onChange: ISimpleEvent<string|null>;
    updateBasedOnSelector(selector: OptionSelector): void;
    select(value: string|null): void;
}

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        KlarnaOnsiteService?: {
            eventName?: string;
        }[];

        // eslint-disable-next-line @typescript-eslint/naming-convention
        Klarna?: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            OnsiteMessaging?: {
                refresh?: {
                    (): string;
                };
            };
        };


        // eslint-disable-next-line @typescript-eslint/naming-convention
        KOSMApp?: {
            updatePurchaseAmount(price: number): Promise<void>;
        };
    }
}

@Component
export class ProductOptionPicker extends ThemeComponent {
    @Prop id!: string;
    @Prop product!: Shopify.Liquid.Product;
    @Prop({default: false}) isFrameset!: boolean;

    @Ref option1!: ProductButtonOption;
    @Ref({ required: false }) option2?: ProductButtonOption;
    @Ref({ required: false }) option3?: ProductButtonOption;

    selector: ManualOptionSelector|null = null;

    get selectedVariant(): Shopify.Liquid.Variant|null {
        const o1 = this.option1.selected;
        const o2 = this.option2?.selected || null;
        const o3 = this.option3?.selected || null;

        return this.product.variants.find((v) =>
            v.option1 == o1 && v.option2 == o2 && v.option3 == o3
        ) || null;
    }

    private _onSelectedVariantChange = new SimpleEvent<Shopify.Liquid.Variant|null>();
    public get onSelectedVariantChange() {
        return this._onSelectedVariantChange.asEvent();
    }

    init() {
        this.selector = new ManualOptionSelector(this.product);

        let oldValue = this.selectedVariant;
        const onAnyChanged = (selectedValue: string|null) => {
            const newValue = this.selectedVariant;
            if (newValue != oldValue) {
                this._onSelectedVariantChange.dispatch(newValue);
            }
            oldValue = newValue;

            // Refresh Klarna placement(s)
            window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
            if (window.Klarna?.OnsiteMessaging?.refresh) {
                window.Klarna.OnsiteMessaging.refresh();
            }
            if (this.selectedVariant !== null && window.KOSMApp) {
                window.KOSMApp.updatePurchaseAmount(this.selectedVariant.price).catch((e) => {
                    this.$log.error(`window.KOSMApp.updatePurchaseAmount failed`, { e });
                });
            }
            this.renderOptions();
        };

        this.$subscribe(this.option1.onChange, onAnyChanged);
        if (this.option2) {this.$subscribe(this.option2.onChange, onAnyChanged);}
        if (this.option3) {this.$subscribe(this.option3.onChange, onAnyChanged);}

        this.renderOptions();
        this._onSelectedVariantChange.dispatch(this.selectedVariant);
    }

    renderOptions() {
        if (!this.selector) {
            return;
        }
        this.selector.select(this.option1.selected, this.option2?.selected || null, this.option3?.selected || null);

        this.option1.updateBasedOnSelector(this.selector);
        this.option2?.updateBasedOnSelector(this.selector);
        this.option3?.updateBasedOnSelector(this.selector);

        this._onSelectedVariantChange.dispatch(this.selectedVariant);

    }

    // Check if option is either Button or Dropdown type
    updateButtonDropdownStyles = (option: ProductButtonOption | undefined) => {
        if (option instanceof ProductButtonOption) {
            option.updateButtonStyles();
        }
    };
}
